//-- Copyright (c) 2021 TMT Insights Group, LLC
import { NavLinks } from '@/__configs/config_types';
import IconLab from '@/components/_common/IconLab/IconLab';
import DataTablePage from '@/components/_common/Layout/DataTablePage';
import SchemaLab from '@/components/_common/SchemaLab';
import AssemblyTemplateEdit from '@/components/AssemblyTemplates/AssemblyTemplateEdit';
import AssemblyTemplates from '@/components/AssemblyTemplates/AssemblyTemplates';
import Login from '@/components/Auth/Login/Login';
import Exceptions from '@/components/Exceptions/Exceptions';
import AvailsAdmin from '@/components/Focus/Admin/AvailsAdmin';
import Avails from '@/components/Focus/Avails/Avails';
import AvailsCalendar from '@/components/Focus/AvailsCalendar/AvailsCalendar';
import AvailsSchedule from '@/components/Focus/AvailsSchedule/AvailsSchedule';
import NewSchedule from '@/components/Focus/AvailsSchedule/NewSchedule/NewSchedule';
import AvailsScheduleDetails from '@/components/Focus/AvailsScheduleDetails/AvailsScheduleDetails';
import Reports from '@/components/Focus/Reports/Reports';
import FileIngestDetail from '@/components/IngestManagement/FileIngest/FileIngestDetail';
import OPOrderConfirmation from '@/components/OrderPlacement/OPOrderConfirmation';
import Packaging from '@/components/Packaging/Packaging';
import { Roles } from '@/constants/constants';
import SchemaPlayground from '../components/_common/SchemaPlayground/SchemaPlayground';
import Dashboard from '../components/Dashboard/Dashboard';
import FileManager from '../components/FileManager/FileManagerUpload/FileManager';
import TransferQueue from '../components/FileManager/TransferQueue/TransferQueue';
import IngestManagement from '../components/IngestManagement';
import Job from '../components/Job/Job';
import Jobs from '../components/Jobs/Jobs';
import NoRoute from '../components/NoRoute/NoRoute';
import NotFound from '../components/NotFound/NotFound';
import OrderPlacement2 from '../components/OrderPlacement/OrderPlacement';
import Orders from '../components/Orders/Orders';
import PendingAssets from '../components/PendingAssets/PendingAssets';
import ProcessingTasks from '../components/ProcessingTasks/ProcessingTasks';
import Profile from '../components/Profile/Profile';
import ProfileBuilder from '../components/ProfileBuilder/ProfileBuilder';
import Profiles from '../components/Profiles/Profiles';
import Tasks from '../components/Tasks/Tasks';
import TitleDetail from '../components/TitleDetail/TitleDetail';
import Titles from '../components/Titles/Titles';

export type PolarisRoute = {
  path: string;
  component: any;
  exact?: boolean;
  role?: string | undefined;
  ignoreScrollBehavior?: boolean;
  calcTitle?: (params?: any) => string;
};

export const authRoutes: PolarisRoute[] = [
  {
    path: '/login',
    component: Login,
    calcTitle: () => 'Login'
  }
];

export const privateRoutes: PolarisRoute[] = [
  {
    // dashboard:
    path: '/',
    component: Dashboard,
    exact: true,
    role: Roles.read_dashboard,
    calcTitle: () => 'Dashboard'
  },
  {
    path: '/dashboard',
    component: Dashboard,
    exact: true,
    role: Roles.read_dashboard,
    calcTitle: () => 'Dashboard'
  },
  // Focus Avails:
  {
    path: '/focus/avails',
    component: Avails,
    exact: true,
    role: Roles.view_avail,
    calcTitle: () => `FOCUS - Avails`
  },
  // Focus Schedule:
  {
    path: '/focus/schedule',
    component: AvailsSchedule,
    exact: true,
    role: Roles.view_schedule,
    calcTitle: () => `FOCUS - Schedule`
  },
  {
    path: '/focus/schedule/new',
    component: NewSchedule,
    exact: true,
    role: Roles.view_schedule,
    calcTitle: () => `FOCUS - Create Schedule`
  },
  {
    path: '/focus/schedule/:id',
    component: AvailsScheduleDetails,
    exact: true,
    role: Roles.view_schedule,
    calcTitle: ({ id }) => `FOCUS - Schedule -  - S${id}`
  },
  // Focus Calendar:
  {
    path: '/focus/releases',
    component: AvailsCalendar,
    exact: true,
    role: Roles.view_releases,
    calcTitle: () => `FOCUS - Releases`
  },
  // Focus Reports:
  {
    path: '/focus/reports',
    component: Reports,
    exact: true,
    role: Roles.view_avail,
    calcTitle: () => `FOCUS - Reports`
  },
  // Focus Calendar:
  {
    path: '/focus/admin',
    component: AvailsAdmin,
    exact: true,
    role: Roles.view_schedule,
    calcTitle: () => `FOCUS - Admin`
  },
  // file manager:
  {
    path: '/file-manager',
    component: FileManager,
    exact: true,
    role: Roles.read_filemanager,
    calcTitle: () => 'File Manager'
  },
  {
    path: '/transfer-queue',
    component: TransferQueue,
    exact: true,
    role: Roles.view_filemanager,
    calcTitle: () => 'Transfer Queue'
  },

  // pendingAssets:
  {
    path: '/pending-assets',
    component: PendingAssets,
    exact: true,
    role: Roles.read_order_job_assets,
    calcTitle: () => 'Pending Assets'
  },

  // tasks:
  {
    path: '/tasks',
    component: Tasks,
    exact: true,
    role: Roles.read_tasks,
    calcTitle: () => 'Tasks'
  },
  {
    path: '/processing-exceptions',
    component: ProcessingTasks,
    exact: true,
    role: Roles.read_tasks,
    calcTitle: () => 'Processing Exceptions'
  },

  // jobs:
  {
    path: '/orders/jobs/:id',
    component: Job,
    exact: false,
    role: Roles.read_jobs,
    calcTitle: ({ id }) => `Jobs - J${id}`
  },
  {
    path: '/jobs/:id',
    component: Job,
    exact: false,
    role: Roles.read_jobs,
    calcTitle: ({ id }) => `Jobs - J${id}`
  },
  {
    path: '/jobs',
    component: Jobs,
    exact: true,
    role: Roles.read_jobs,
    calcTitle: () => `Jobs`
  },
  // orders:
  {
    path: '/orders',
    component: Orders,
    exact: true,
    role: Roles.read_orders,
    calcTitle: () => `Orders`
  },
  // orderPlacement:
  {
    path: '/orders-v2/new',
    component: OrderPlacement2,
    exact: true,
    role: Roles.write_orders,
    calcTitle: () => `New Order`
  },
  {
    path: '/orders-v2/confirmation/:orderId',
    component: OPOrderConfirmation,
    exact: true,
    role: Roles.write_orders,
    calcTitle: ({ orderId }) => `Confirm Order - ${orderId}`
  },
  // exceptions
  {
    path: '/exceptions',
    component: Exceptions,
    exact: true,
    role: Roles.view_event,
    calcTitle: () => `Exceptions`
  },
  // profileBuilder:
  {
    path: '/profiles/new',
    component: ProfileBuilder,
    exact: true,
    role: Roles.write_profiles,
    calcTitle: () => `Profile - New`
  },
  // profiles:
  {
    path: '/profiles',
    component: Profiles,
    exact: true,
    role: Roles.read_profiles,
    calcTitle: () => `Customer Profiles`
  },
  {
    path: '/profiles/:id',
    component: Profile,
    exact: true,
    role: Roles.read_profiles,
    calcTitle: ({ id }) => `Profile - ${id}`
  },
  // titles:
  {
    path: '/titles/:id',
    component: TitleDetail,
    exact: false,
    role: Roles.read_titles,
    ignoreScrollBehavior: true,
    calcTitle: ({ id }) => `Title - E${id}`
  },
  {
    path: '/titles',
    component: Titles,
    exact: true,
    role: Roles.read_titles,
    calcTitle: () => `Titles`
  },
  {
    path: '/ingest-management/:id',
    component: FileIngestDetail,
    exact: true,
    role: Roles.read_fileingest,
    calcTitle: () => `Ingest Manager`
  },
  {
    path: '/ingest-management',
    component: IngestManagement,
    exact: true,
    role: Roles.read_fileingest,
    calcTitle: () => `Ingest Manager`
  },
  {
    path: '/packaging/:id',
    component: Packaging,
    exact: true,
    role: Roles.read_content_assembly_task,
    calcTitle: () => `Packaging`
  },
  {
    path: '/assembly-templates/',
    component: AssemblyTemplates,
    exact: true,
    role: Roles.read_content_assembly_task,
    calcTitle: () => `Assembly Templates`
  },
  {
    path: '/assembly-templates/edit/:id',
    component: AssemblyTemplateEdit,
    exact: true,
    role: Roles.read_content_assembly_task,
    calcTitle: ({ id }) => `Edit Assembly Template - ${id}`
  },

  // internal use only
  {
    path: '/schema-playground',
    component: SchemaPlayground,
    exact: true,
    role: Roles.read_all,
    calcTitle: () => `Schema Playground`
  },
  // noRoutes:
  {
    path: '/no-access',
    exact: true,
    component: NoRoute,
    calcTitle: () => `Not Allowed`
  }
];

export const publicRoutes: PolarisRoute[] = [
  {
    path: '/icon-lab',
    component: IconLab,
    exact: true,
    role: Roles.read_all,
    calcTitle: () => `Icon Lab`
  },
  {
    path: '/schema-lab',
    exact: true,
    component: SchemaLab,
    calcTitle: () => 'Schema Lab'
  },
  {
    path: '/page-not-found',
    exact: true,
    component: NotFound
  },
  {
    path: '*',
    component: NotFound
  }
];

export const getPolarisRouteFromNavLinks = (clientNavLinks: NavLinks): PolarisRoute[] => {
  // if any exist with children, treat the children as the routes instead
  const navLinksWithChildren = clientNavLinks.filter((config) => config.children);
  let navLinks = clientNavLinks.filter((config) => !config.children);
  if (navLinksWithChildren.length) {
    navLinksWithChildren.forEach((config) => {
      if (config.to?.length) {
        navLinks.push({ ...config, children: undefined });
      }
      if (config.children?.length) {
        navLinks = navLinks.concat(config.children);
      }
    });
  }

  const response = navLinks.map((config) => {
    let ComponentToUse = config.component;
    let ignoreScrollBehaviorPreset: boolean = false;

    if (!ComponentToUse && config?.to) {
      const defaultPrivateRouteKeys = privateRoutes.map((config) => config.path);
      const foundPresetIndex = defaultPrivateRouteKeys.indexOf(config?.to);
      ComponentToUse = privateRoutes[foundPresetIndex]?.component;
      ignoreScrollBehaviorPreset = privateRoutes[foundPresetIndex]?.ignoreScrollBehavior || false;
    } else if (!ComponentToUse) {
      ComponentToUse = () => <div>No component found.</div>;
    }

    return {
      path: config.to,
      component: () => (ComponentToUse ? <ComponentToUse /> : <DataTablePage pageConfig={config} />),
      exact: config.exact,
      role: config.role,
      ignoreScrollBehavior: ignoreScrollBehaviorPreset,
      calcTitle: () => config.label
    } as PolarisRoute;
  });
  return response;
};
